import { useEffect, useState, useSyncExternalStore } from "react";
import { getToken, logout, me, setToken, validProfile, type AuthResult, type Profile } from "./api";
import { isCompassSyncOn, subscribeCompassSync } from "./state/compassSync";
import { CompassLive } from "./components/CompassLive";
import { RideKeeper, Roll20Rebinder, TokenFollowKeeper } from "./components/TokenLinks";
import { EffectsProvider } from "./components/EffectsHost";
import { PrefsSync } from "./components/PrefsSync";
import { LoginScreen } from "./components/LoginScreen";
import { Shell } from "./components/Shell";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { LayoutProvider } from "./state/store";
import type { Layout } from "./state/types";

type Session = { profile: Profile; layout: Layout | null; revision: number } | null;

export function App() {
  const [session, setSession] = useState<Session>(null);
  const [checking, setChecking] = useState(Boolean(getToken()));
  // Per-device switch: when off, the two Compass lanes never mount, so nothing is pushed to or pulled from the Compass app.
  const compassOn = useSyncExternalStore(subscribeCompassSync, isCompassSyncOn, isCompassSyncOn);

  useEffect(() => {
    if (!getToken()) return;
    me().then((r) => { if (validProfile(r.profile)) setSession({ profile: r.profile, layout: r.layout, revision: r.revision }); else setToken(""); })
      .catch(() => setToken(""))
      .finally(() => setChecking(false));
  }, []);

  // Login does not return the layout revision; fetch it so the first autosave carries the right one.
  const onAuth = async (result: AuthResult) => {
    setToken(result.token);
    const r = await me();
    // A malformed /me (a non-JSON 200 already throws in api(); a valid-JSON body with no
    // profile would not) must not become a session — clear the token and surface it so the
    // login screen shows the error rather than the app blank-crashing on session.profile.id.
    if (!validProfile(r.profile)) { setToken(""); throw new Error("Signed in, but the service did not return a valid profile. Please try again."); }
    setSession({ profile: r.profile, layout: r.layout, revision: r.revision });
  };
  const signOut = async () => { await logout(); setToken(""); setSession(null); };

  if (checking) return <div className="gate"><p className="muted">Opening your DM profile…</p></div>;
  // The validProfile guard here is the last line against a blank-crash: session is only
  // ever set with a valid profile above, but rendering the app on a profile-less session
  // would read session.profile.id and white-screen a brand-new user with no recovery.
  if (!session || !validProfile(session.profile)) return <LoginScreen onAuth={onAuth} />;
  return (
    <ErrorBoundary label="DM Screen">
      <LayoutProvider key={session.profile.id} profile={session.profile} initial={session.layout} initialRevision={session.revision}>
        {compassOn && <CompassLive />}
        <TokenFollowKeeper />
        <RideKeeper />
        <Roll20Rebinder />
        {compassOn && <PrefsSync />}
        <EffectsProvider>
          <Shell onSignOut={signOut} />
        </EffectsProvider>
      </LayoutProvider>
    </ErrorBoundary>
  );
}
